/**
 * Coordinates out of `applications.google_maps` (spec-desk-venue-location,
 * CONTEXT D-03 step 2, VLOC-02).
 *
 * The editor copies whatever the post linked into `google_maps`. Spec §2
 * evidence (prod, 60 days, 149 non-null values): 82 empty strings, 62
 * `maps.app.goo.gl` short links, 4 other URLs and 1 plain "lat,lng" pair. Three
 * shapes carry coordinates the browser can read:
 *
 * - a plain pair: `38.059131,-8.779732`
 * - a place URL: `.../maps/place/Foo/@38.05,-8.77,17z/data=...!3d38.0591!4d-8.7797...`.
 *   Its `!3d<lat>!4d<lng>` pair is the place's pin and wins; the `@lat,lng`
 *   pair is only the map view's centre, used when there is no `!3d!4d`
 * - a search URL with `?q=lat,lng` (or `&q=`), comma possibly `%2C`, `+` or spaces
 *
 * Short links (`maps.app.goo.gl`, `goo.gl/maps`) need a server-side redirect
 * the browser cannot follow (CORS), so they are ignored. This module never
 * fetches anything.
 *
 * The result is the form's `venue_location` string, `lat,lng`, built from the
 * captured number text (the source precision is kept). Numbers outside lat
 * [-90, 90] / lng [-180, 180] and anything else give null. The patterns are
 * linear (no nested quantifiers).
 *
 * Pure: no Supabase, no React, no i18n.
 */

const SHORT_LINK = /(?:^|[/.])goo\.gl(?:[/?#]|$)/i
const PLAIN_PAIR = /^(-?\d{1,3}(?:\.\d+)?)\s*,\s*\+?(-?\d{1,3}(?:\.\d+)?)$/
// A place URL's own pin, in its data segment: `!3d<lat>!4d<lng>` (review WR-05).
const PLACE_PIN = /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/
const AT_PAIR = /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/
const QUERY_PAIR = /[?&]q=(-?\d{1,3}\.\d+)\s*,\s*\+?(-?\d{1,3}\.\d+)/

const inRange = (latText: string, lngText: string): boolean => {
  const lat = Number(latText)
  const lng = Number(lngText)
  return Number.isFinite(lat) && Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
}

const decode = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** The form's 'lat,lng' from a google_maps value, or null when it holds none. */
export const parseGoogleMapsCoords = (value: string | null | undefined): string | null => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return null
  if (SHORT_LINK.test(trimmed)) return null
  const text = decode(trimmed)
  // The `@` pair is the map view's centre, often hundreds of metres off the
  // place when the side panel is open, so the place's `!3d!4d` pin comes first.
  const match =
    text.match(PLAIN_PAIR) ?? text.match(PLACE_PIN) ?? text.match(AT_PAIR) ?? text.match(QUERY_PAIR)
  if (!match) return null
  const [, latText, lngText] = match
  if (!inRange(latText, lngText)) return null
  return `${latText},${lngText}`
}
